// ─────────────────────────────────────────────────────────────────
// Glass-i — Accounts audit log + attachments API client.
//
// Stage 6B.7D    — read-only audit log + metadata-only attachments.
// Stage 6B.7D-2  — binary upload (multipart/form-data) + signed-URL
//                  download.
//
// The accountAttachmentsApi.upload uses FormData under the hood so
// multer can parse the multipart payload server-side. The signed
// download URL is returned as JSON; the caller opens it in a new
// tab — the raw bucket layout / object key NEVER reaches the
// browser.
// ─────────────────────────────────────────────────────────────────

import client from '../../../api/client';

const BASE = '/api/erp/accounts';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

export const accountAuditApi = {
  list: (params = {}) =>
    client.get(`${BASE}/audit-log`, { params }).then(unwrap)
};

export const accountAttachmentsApi = {
  list: ({ entityType, entityId, includeDeleted } = {}) =>
    client.get(`${BASE}/attachments`, {
      params: {
        entityType, entityId,
        ...(includeDeleted ? { includeDeleted: 'true' } : {})
      }
    }).then(unwrap),

  // Stage 6B.7D-2 — multipart upload. `file` is a File / Blob from
  // an <input type="file"> element. We use FormData so axios picks
  // the right Content-Type boundary header automatically.
  upload: ({ entityType, entityId, description, file }) => {
    const form = new FormData();
    form.append('entityType', entityType);
    form.append('entityId',   entityId);
    if (description) form.append('description', description);
    form.append('file', file);
    return client.post(`${BASE}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(unwrap);
  },

  // Stage 6B.7D-2 — request a short-lived signed URL. The response
  // contains { downloadUrl, expiresInSeconds, fileName, mimeType }.
  // Callers should open downloadUrl in a new tab; the URL expires
  // server-side after expiresInSeconds.
  getDownloadUrl: (id) =>
    client.get(`${BASE}/attachments/${encodeURIComponent(id)}/download`).then(unwrap),

  remove: (id) =>
    client.delete(`${BASE}/attachments/${encodeURIComponent(id)}`).then(unwrap)
};
